import { useState } from "react"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { z } from "zod"

import { CirclePlus, Trash } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"

type LanguageSelectProps = {
  label?: string
  selectedLanguage: string
  availableLanguages: string[]
  onChangeLanguage: (language: string) => void
  onAddLanguage: (language: string) => void
  onDeleteLanguage?: (language: string) => void
}

export function LanguageSelect(props: LanguageSelectProps) {
  const { t } = useTranslation()
  const {
    label,
    selectedLanguage,
    availableLanguages,
    onChangeLanguage,
    onAddLanguage,
    onDeleteLanguage,
  } = props

  const [showAddLanguageDialog, setShowAddLanguageDialog] = useState(false)

  const formSchema = z.object({
    language: z
      .string()
      .min(1, { message: t("textinputdropdown.language_required") })
      .max(20),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { language: "" },
  })

  function handleSelectChange(value: string) {
    if (value === "add_new") {
      setShowAddLanguageDialog(true)
      return
    }
    onChangeLanguage(value)
  }

  function handleDelete(languageToDelete: string) {
    if (!onDeleteLanguage) return
    onDeleteLanguage(languageToDelete)
  }

  function handleAddNewLanguage(values: z.infer<typeof formSchema>) {
    const languageAlreadyExists = availableLanguages
      .map((lang) => lang.toLowerCase())
      .includes(values.language.trim().toLowerCase())

    if (languageAlreadyExists) {
      form.setError("language", {
        type: "manual",
        message: t("textinputdropdown.language_already_exists"),
      })
      return
    }

    const newLanguage = values.language.trim()
    const capitalizedLanguage = newLanguage.charAt(0).toUpperCase() + newLanguage.slice(1)

    onAddLanguage(capitalizedLanguage)
    onChangeLanguage(capitalizedLanguage)

    form.reset()
    setShowAddLanguageDialog(false)
    toast.success(`${capitalizedLanguage} ${t("textinputdropdown.language_added")}`)
  }

  return (
    <section className="flex w-full flex-col gap-1">
      {label ? <Label>{label}</Label> : null}
      <Select
        defaultValue={selectedLanguage}
        value={selectedLanguage}
        onValueChange={handleSelectChange}
        onOpenChange={(open) => {
          if (!open && !selectedLanguage) {
            setShowAddLanguageDialog(true)
          }
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder={t("textinputdropdown.select")} />
        </SelectTrigger>
        <SelectContent>
          {availableLanguages.map((availableLanguage) => (
            <section key={availableLanguage} className="flex items-center">
              <SelectItem
                key={availableLanguage}
                className="flex w-full flex-row"
                value={availableLanguage}
              >
                <p>{availableLanguage}</p>
              </SelectItem>
              {onDeleteLanguage ? (
                <Button size="icon" variant="icon" onClick={() => handleDelete(availableLanguage)}>
                  <Trash className="z-20 size-4" />
                </Button>
              ) : null}
            </section>
          ))}
          <Separator className="my-[6px]" />
          <SelectItem value="add_new">
            <section className="flex items-start gap-2">
              <CirclePlus className="size-4" />
              <p>{t("textinputdropdown.add_new")}</p>
            </section>
          </SelectItem>
        </SelectContent>
      </Select>

      <Dialog open={showAddLanguageDialog} onOpenChange={setShowAddLanguageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("textinputdropdown.title")}</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(handleAddNewLanguage)}>
              <FormField
                control={form.control}
                name="language"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("textinputdropdown.language")}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("textinputdropdown.placeholder")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit">{t("textinputdropdown.add")}</Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </section>
  )
}
